/**
 * Admin failures, in the admin’s language: a title naming the operation
 * that failed, over the reason the server returned. Saves, loads, deletes
 * and other page actions share this contract.
 * Why is a refusal portal-backend gives by its code (refusals.ts) — a session
 * that would start after its campaign's end, a campaign end before a session
 * of its — said in the admin's language with the campaign and the instant it
 * names; or else the words the server's answer carried, as the game says a
 * refusal with no code under "Unable to join". A failure the server gave no
 * words for (no answer at all, an empty body) is the title alone: the
 * client's own English ("Failed to fetch", "Request failed (500)") is not
 * shown. Every admin form that saves reads it: the world, session, game,
 * cohort, cohort students, campaign, chapter date, user and family forms, and
 * the campaign page's session scheduler.
 */
import type { TFunction } from 'i18next';
import { ApiError } from '../../api/_shared/api-error';
import { sessionRefusalOf } from '../../api/admin/sessions/refusals';
import { formatDateTime } from '../../utils/format-date';

/** What a form's message says (FormMessage): a title over the words that say why, if any, or with no title its words alone, never nothing. */
export type FormFailure = TitledFailure | { title?: undefined; detail: string };

/** A failed operation: a localized title, with server details when available. */
type TitledFailure = { title: string; detail: string | null };

/** What a failed save was saving, by the key of the title that names it. */
const NOT_SAVED = {
  world: 'admin.saveFailed.world',
  session: 'admin.saveFailed.session',
  game: 'admin.saveFailed.game',
  cohort: 'admin.saveFailed.cohort',
  cohortStudents: 'admin.saveFailed.cohortStudents',
  campaign: 'admin.saveFailed.campaign',
  chapterDate: 'admin.saveFailed.chapterDate',
  user: 'admin.saveFailed.user',
  userPhoto: 'admin.saveFailed.userPhoto',
  family: 'admin.saveFailed.family',
} as const;

/** A failed save in the admin's words: what was not saved, over a refusal in their language or the words the server's answer carried. */
export function saveFailure(t: TFunction, e: unknown, saved: keyof typeof NOT_SAVED): TitledFailure {
  return failure(t, e, NOT_SAVED[saved]);
}

/** A refusal portal-backend gives by its code, in the admin's language; null for any other failure. */
function refusalWords(t: TFunction, e: unknown): string | null {
  const refusal = sessionRefusalOf(e);
  if (!refusal) return null;
  return refusal.code === 'SESSION_AFTER_CAMPAIGN_END'
    ? t('admin.sessions.refusals.afterCampaignEnd', { campaign: refusal.campaignName, end: formatDateTime(refusal.campaignEndUtc) })
    : t('admin.sessions.refusals.campaignEndBeforeSession', { campaign: refusal.campaignName, start: formatDateTime(refusal.sessionStartUtc) });
}

/** Loads name the resource that could not be read, without presenting an empty result as success. */
const NOT_LOADED = {
  cohorts: 'admin.loadFailed.cohorts', games: 'admin.loadFailed.games', users: 'admin.loadFailed.users',
  worlds: 'admin.loadFailed.worlds', sessions: 'admin.loadFailed.sessions', students: 'admin.loadFailed.students',
  campaigns: 'admin.loadFailed.campaigns', campaign: 'admin.loadFailed.campaign',
  families: 'admin.loadFailed.families', family: 'admin.loadFailed.family', devices: 'admin.loadFailed.devices',
  launch: 'admin.loadFailed.launch', importHistory: 'admin.loadFailed.importHistory', importJob: 'admin.loadFailed.importJob',
} as const;
const NOT_DELETED = {
  cohort: 'admin.deleteFailed.cohort', game: 'admin.deleteFailed.game', user: 'admin.deleteFailed.user',
  world: 'admin.deleteFailed.world', session: 'admin.deleteFailed.session', campaign: 'admin.deleteFailed.campaign',
} as const;
const REQUEST_FAILED = {
  preview: 'requestErrors.preview', execute: 'requestErrors.execute',
  launch: 'requestErrors.launch', revoke: 'requestErrors.revoke',
  resetWorldSave: 'admin.worlds.resetSave.failed',
} as const;

/** The shared presentation contract for saves, loads and actions: local title, server detail only. */
function failure(t: TFunction, e: unknown, titleKey: string): TitledFailure {
  return { title: t(titleKey), detail: refusalWords(t, e) ?? (e instanceof ApiError ? e.serverMessage : null) };
}

export function loadFailure(t: TFunction, e: unknown, resource: keyof typeof NOT_LOADED): TitledFailure {
  return failure(t, e, NOT_LOADED[resource]);
}
export function deleteFailure(t: TFunction, e: unknown, resource: keyof typeof NOT_DELETED): TitledFailure {
  return failure(t, e, NOT_DELETED[resource]);
}
export function requestFailure(t: TFunction, e: unknown, action: keyof typeof REQUEST_FAILED): TitledFailure {
  return failure(t, e, REQUEST_FAILED[action]);
}
